import { Component, OnInit, Input, Output, EventEmitter, ViewChild, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LoggingService } from '@services/logging.service';
import { DialogService } from '@services/dialog.service';
import * as Constants from '@shared/_constants';
import { BalanceDataInterestRate } from './interest-rate.model';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { InterestRateService } from '@services/interest-rate.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RegularExpressionPattern } from '@shared/_constants';
import { AdjustmentModalTemplateComponent } from '../../../adjustment-modal-template/adjustment-modal-template.component';

@Component({
  selector: 'app-interest-rate',
  templateUrl: './interest-rate.component.html',
  styleUrls: ['./interest-rate.component.css'],
})
export class InterestRateComponent implements OnInit, OnDestroy {
  @ViewChild(AdjustmentModalTemplateComponent, { static: false }) modal: AdjustmentModalTemplateComponent;

  @Input() readOnly = false;
  @Input() adjustmentsTestedPartyBalanceDataId: number;
  @Input() interestRateData: BalanceDataInterestRate = new BalanceDataInterestRate();
  @Input() testedPartyId: number;
  @Output() selectedInterestRateData: EventEmitter<BalanceDataInterestRate> = new EventEmitter<BalanceDataInterestRate>();

  regularExpressionPattern: typeof RegularExpressionPattern = RegularExpressionPattern;
  irFileDropdownDefaultVal = Constants.InterestRateDialogMessages.IR_DROPDOWN_DEFAULT_VAL;
  customErrorMessage = Constants.InterestRateDialogMessages.DECIMAL_ONLY_MSG;
  customRequiredMessage = '';
  customIRRequiredMessage = '';

  titleText = Constants.InterestRateDialogMessages.ADD_INTEREST_RATE_TEXT + Constants.InterestRateDialogMessages.TITLE_TEXT;
  saveButtonText = Constants.InterestRateDialogMessages.ADD_INTEREST_RATE_TEXT;
  successMsg = Constants.InterestRateDialogMessages.ADD_SUCCESS_MSG;
  failureMsg = Constants.InterestRateDialogMessages.ADD_FAILURE_MSG;
  fileExtension: string = Constants.InterestRateDialogMessages.ACCEPTED_FILE_EXTENSTIONS;

  balanceDataInterestRate: BalanceDataInterestRate = new BalanceDataInterestRate();
  public url: string;
  showLoader = false;
  noSpinner = true;

  isInterestRateFormValid = false;
  unsavedChanges = false;

  files: Array<any> = [];
  fileLength = '';
  filename = '';
  irFileList: Array<any> = [];

  interestRateForm = this.fb.group(
    {
      interestRate: [0, [Validators.required, Validators.pattern(RegularExpressionPattern.NumberWithDecimalPattern)]],
      interestRateSource: [''],
      interestRateSourceDocPath: [''],
      adjustmentsTestedPartyBalanceDataId: 0,
    },
    { updateOn: 'change' },
  );

  private onChange: (value: any) => void = () => null;

  constructor(
    private activeModal: NgbActiveModal,
    private logger: LoggingService,
    private dialogService: DialogService,
    private interestRateService: InterestRateService,
    private fb: UntypedFormBuilder,
  ) {}

  private emitValueChanges() {
    this.interestRateForm.valueChanges.subscribe((data) => {
      // TODO Unsubscribe in ngOnDestroy
      this.onChange(data);
    });
  }

  private destroy$ = new Subject();

  ngOnInit() {
    this.emitValueChanges();
    try {
      this.showLoader = true;
      if (this.interestRateData) {
        if (this.interestRateData.balanceDataInterestRateId > 0) {
          this.titleText = Constants.InterestRateDialogMessages.EDIT_INTEREST_RATE_TEXT + Constants.InterestRateDialogMessages.TITLE_TEXT;
          this.saveButtonText = Constants.InterestRateDialogMessages.EDIT_INTEREST_RATE_TEXT;
          this.successMsg = Constants.InterestRateDialogMessages.EDIT_SUCCESS_MSG;
          this.failureMsg = Constants.InterestRateDialogMessages.EDIT_FAILURE_MSG;
        }
        this.bindData();
        this.getExistingFileList();
        this.getIRFileSize();
      }

      this.interestRateForm.valueChanges.subscribe(() => {
        this.setInterestRateConditionalValidators();
      });
    } finally {
      this.showLoader = false;
    }
  }

  bindData() {
    this.interestRateForm.patchValue({
      balanceDataInterestRateId: this.interestRateData.balanceDataInterestRateId,
      interestRate: this.interestRateData.interestRate,
      interestRateSource: this.interestRateData.interestRateSource,
      interestRateSourceDocPath: this.interestRateData.interestRateSourceDocPath,
      adjustmentsTestedPartyBalanceDataId: this.adjustmentsTestedPartyBalanceDataId,
    });
  }
  checkLoader() {
    return this.noSpinner ? true : false;
  }
  getExistingFileList() {
    try {
      this.noSpinner = false;
      this.interestRateService
        .getBalanceDataInterestRateFileList(this.testedPartyId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((_irFileList) => {
          this.irFileList = _irFileList;
          this.noSpinner = true;
        });
    } catch {
      this.noSpinner = true;
    }
  }

  getIRFileSize() {
    if (this.interestRateData.interestRateSourceDocPath) {
      this.interestRateService
        .getInterestRateFileSize(this.createFormData())
        .pipe(takeUntil(this.destroy$))
        .subscribe((_fileLength) => {
          this.fileLength = _fileLength;
        });
    }
  }

  changeIRFile(file: string) {
    this.filename = file;
    this.setInterestRateConditionalValidators();
  }

  onSubmit() {
    this.balanceDataInterestRate = {
      ...this.balanceDataInterestRate,
      interestRate: this.interestRateForm.value.interestRate,
      interestRateSource: this.interestRateForm.value.interestRateSource,
      interestRateSourceDocPath: this.interestRateForm.value.interestRateSourceDocPath,
      adjustmentsTestedPartyBalanceDataId: this.adjustmentsTestedPartyBalanceDataId,
    };
    this.selectedInterestRateData.emit(this.balanceDataInterestRate);
  }

  upload(file) {
    this.logger.info(this.constructor.name, 'upload', 'Start');
    try {
      this.showLoader = true;
      if (file) {
        this.unsavedChanges = true;
        this.files = file;
        this.setInterestRateConditionalValidators();
      }
    } finally {
      this.showLoader = false;
    }
  }

  deleteFile(file) {
    this.logger.info(this.constructor.name, 'deleteFile', 'Start');
    try {
      this.showLoader = true;
      if (file) {
        this.unsavedChanges = true;
        this.files = Array.from(this.files).filter((f) => f.name !== file.name);
      }
      this.balanceDataInterestRate.interestRateSourceDocPath = '';
      this.setInterestRateConditionalValidators();
    } finally {
      this.showLoader = false;
      this.logger.info(this.constructor.name, 'deleteFile', 'End');
    }
  }

  save() {
    this.logger.info(this.constructor.name, 'save', 'Start');
    try {
      this.showLoader = true;
      this.interestRateService
        .uploadInterestRateFile(this.createFormData(), '', this.adjustmentsTestedPartyBalanceDataId, this.files)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          this.balanceDataInterestRate = data;

          if (this.balanceDataInterestRate) {
            this.dialogService.acknowledgeInterestRate('Result', this.successMsg, 'OK');
            this.unsavedChanges = false;
            this.close(this.balanceDataInterestRate);
          } else {
            this.dialogService.acknowledgeInterestRate('Result', this.failureMsg, 'OK');
          }
        });
    } catch (e) {
      this.logger.error(this.constructor.name, 'save', e);
    } finally {
      this.showLoader = false;
      this.logger.info(this.constructor.name, 'save', 'End');
    }
  }

  IsSourceContainerVisible() {
    return this.interestRateData && this.interestRateData.interestRateSource ? true : false;
  }

  IsDocumentContainerVisible() {
    return this.interestRateForm.value.interestRateSourceDocPath ? true : false;
  }

  IsDropdownVisible() {
    return this.files.length <= 0 && this.irFileList && this.irFileList.length > 0;
  }

  createFormData() {
    const formData: FormData = new FormData();
    if (this.files && this.files.length > 0) {
      this.fileLength = this.files.length.toString();
      for (let j = 0; j < this.files.length; j++) {
        formData.append('file[]', this.files[j], this.files[j].name);
      }
    }
    formData.append('hostname', this.url);
    formData.append('testedPartyId', this.testedPartyId.toString());
    formData.append(
      'balanceDataInterestRateId',
      this.interestRateData && this.interestRateData.balanceDataInterestRateId ? this.interestRateData.balanceDataInterestRateId.toString() : '0',
    );
    formData.append('adjustmentsTestedPartyBalanceDataId', this.adjustmentsTestedPartyBalanceDataId.toString());
    formData.append(
      'interestRateSource',
      this.interestRateForm.value.interestRateSource != null ? this.interestRateForm.value.interestRateSource : '',
    );
    if (this.filename != this.irFileDropdownDefaultVal && this.filename != '') {
      formData.append('interestRateBlobStoragePath', this.filename);
    } else {
      formData.append(
        'interestRateBlobStoragePath',
        this.interestRateForm.value.interestRateSourceDocPath != null ? this.interestRateForm.value.interestRateSourceDocPath : '',
      );
    }
    formData.append('interestRatePercentage', this.interestRateForm.value.interestRate != null ? this.interestRateForm.value.interestRate : '0');

    return formData;
  }

  deleteInterestRateSourceOrDoc(deleteFor) {
    let deleteSuccess = '';
    let deleteFailure = '';
    let valueToBeChecked = false;
    let deleteConfirmMsg = '';
    if (deleteFor == 'source') {
      valueToBeChecked = this.interestRateForm.value.interestRateSourceDocPath == '' ? true : false;
      deleteSuccess = Constants.InterestRateDialogMessages.DELETE_SOURCE_SUCCESS_MSG;
      deleteFailure = Constants.InterestRateDialogMessages.DELETE_SOURCE_FAILURE_MSG;
      deleteConfirmMsg = Constants.InterestRateDialogMessages.DELETE_IRSOURCE_CONFIRM;
    } else if (deleteFor == 'doc') {
      valueToBeChecked = this.interestRateForm.value.interestRateSource == '' ? true : false;
      deleteSuccess = Constants.InterestRateDialogMessages.DELETE_FILE_SUCCESS_MSG;
      deleteFailure = Constants.InterestRateDialogMessages.DELETE_FILE_FAILURE_MSG;
      deleteConfirmMsg = Constants.InterestRateDialogMessages.DELETE_IRFILE_CONFIRM;
    }

    this.logger.info(this.constructor.name, 'updateIRSourceOrDocData', 'Start');

    if (valueToBeChecked) {
      this.dialogService.acknowledge(
        Constants.InterestRateDialogMessages.RESULT_TITLE,
        Constants.InterestRateDialogMessages.IR_FILE_SOURCE_REQD_MSG,
        Constants.InterestRateDialogMessages.OK_TITLE,
      );
    } else {
      this.dialogService
        .confirm(
          Constants.InterestRateDialogMessages.CONFITM_TITLE,
          deleteConfirmMsg,
          Constants.InterestRateDialogMessages.CONFIRM_YES,
          Constants.InterestRateDialogMessages.CONFIRM_NO,
        )
        .then((confirmationResult) => {
          if (confirmationResult === Constants.ConfirmationResult.yes) {
            if (deleteFor == 'source') {
              this.interestRateForm.get('interestRateSource').setValue('', { emitEvent: true });
            } else if (deleteFor == 'doc') {
              this.interestRateForm.get('interestRateSourceDocPath').setValue('', { emitEvent: true });
            }

            try {
              this.showLoader = true;
              this.interestRateService
                .deleteInterestRateFileOrSource(this.createFormData(), '', this.adjustmentsTestedPartyBalanceDataId)
                .pipe(takeUntil(this.destroy$))
                .subscribe((_balanceDataInterestRate) => {
                  this.balanceDataInterestRate = _balanceDataInterestRate;

                  if (this.balanceDataInterestRate) {
                    this.dialogService.acknowledgeInterestRate(
                      Constants.InterestRateDialogMessages.RESULT_TITLE,
                      deleteSuccess,
                      Constants.InterestRateDialogMessages.OK_TITLE,
                    );
                    this.unsavedChanges = false;
                    this.close(this.balanceDataInterestRate);
                  } else {
                    this.dialogService.acknowledgeInterestRate(
                      Constants.InterestRateDialogMessages.RESULT_TITLE,
                      deleteFailure,
                      Constants.InterestRateDialogMessages.OK_TITLE,
                    );
                  }
                });
            } catch (e) {
              this.logger.error(this.constructor.name, 'updateIRSourceOrDocData', e);
            } finally {
              this.showLoader = false;
              this.logger.info(this.constructor.name, 'updateIRSourceOrDocData', 'End');
            }
          }
        });
    }
  }

  exportInterestRateFile() {
    this.interestRateService.blockUI();
    this.interestRateService.downloadInterestRateFile(this.createFormData(), '', this.interestRateForm.value.interestRateSourceDocPath).subscribe();

    this.logger.info(this.constructor.name, 'exportInterestRateFile', 'End');
    this.interestRateService.unblockUI();
  }

  close(balanceDataInterestRate: BalanceDataInterestRate = null) {
    if (this.unsavedChanges) {
      this.dialogService
        .confirm(
          Constants.InterestRateDialogMessages.CONFITM_TITLE,
          Constants.InterestRateDialogMessages.MANAGE_IR_UNSAVED_MSG,
          Constants.InterestRateDialogMessages.CONFIRM_YES,
          Constants.InterestRateDialogMessages.CONFIRM_NO,
        )
        .then((confirmationResult) => {
          if (confirmationResult === Constants.ConfirmationResult.yes) {
            this.activeModal.close(balanceDataInterestRate);
          }
        });
    } else {
      this.activeModal.close(balanceDataInterestRate);
    }
  }

  get isBalanceSheetSaved(): boolean {
    if (this.adjustmentsTestedPartyBalanceDataId > 0) return true;

    return false;
  }

  setInterestRateConditionalValidators() {
    const interestRateSourceControl = this.interestRateForm.get('interestRateSource');
    const interestRateSourceDocPathControl = this.interestRateForm.get('interestRateSourceDocPath');

    if (
      !this.interestRateForm.value.interestRateSource &&
      (!this.files || this.files.length <= 0) &&
      (this.filename == this.irFileDropdownDefaultVal || this.filename == '') &&
      !this.interestRateForm.value.interestRateSourceDocPath
    ) {
      if (typeof this.interestRateForm.value.interestRateSource != 'undefined' && this.interestRateForm.value.interestRateSource != null) {
        interestRateSourceControl.setValidators([Validators.required]);
        interestRateSourceDocPathControl.setValidators(null);
      } else {
        interestRateSourceControl.setValidators(null);
        interestRateSourceDocPathControl.setValidators([Validators.required]);
      }
      if (
        typeof this.interestRateForm.value.interestRateSourceDocPath != 'undefined' &&
        this.interestRateForm.value.interestRateSourceDocPath != null
      ) {
        interestRateSourceControl.setValidators(null);
        interestRateSourceDocPathControl.setValidators([Validators.required]);
      } else {
        interestRateSourceControl.setValidators([Validators.required]);
        interestRateSourceDocPathControl.setValidators(null);
      }
      this.customRequiredMessage = Constants.InterestRateDialogMessages.INTERESTRATESOURCE_VALIDATION_MSG;
    } else {
      interestRateSourceControl.setValidators(null);
      interestRateSourceDocPathControl.setValidators(null);
      this.customRequiredMessage = '';
    }
    this.isInterestRateFormValid = this.customRequiredMessage == '' && this.interestRateForm.valid ? true : false;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }
}
